/**
 * Recommendation Policy input and output shapes (S3-01).
 * Requirement sources: Elevate PRS DRAFT 0.10.7 Appendix D section D.1
 * (inputs), Appendix A (status vocabulary), section 3.3 (assigned, selected
 * and effective development level), CFX-01/CFX-03 (framework registry),
 * GOV-06 (operator precedence), REC-02 (recommendation contents).
 *
 * Vocabularies are REUSED from the ICD module and never redefined here:
 * GradeV1, DevelopmentLevelV1, DevelopmentLevelSourceV1, PathwayStatusV1,
 * BehaviourOriginV1, GradeLabelV1, HowManyV1, HowOftenV1, TemOutcomeV1,
 * LimitingFactorV1.
 */
import { z } from 'zod';

import {
  BehaviourOriginV1,
  DevelopmentLevelSourceV1,
  DevelopmentLevelV1,
  GradeLabelV1,
  GradeV1,
  HowManyV1,
  HowOftenV1,
  LimitingFactorV1,
  PathwayStatusV1,
  TemOutcomeV1,
} from '../icd/enums';

/** Calendar date in ISO YYYY-MM-DD form; the only date shape the policy reads. */
export const IsoDateV1 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

/**
 * The INT-08 per-competency capture group: the three ICAO assessment
 * dimensions of ICD sections 8.1 to 8.3 and the limiting factor of 8.4. The
 * four vocabularies are the ICD's own and are reused, never restated.
 *
 * The GROUP is nullable on the input above, and ICD 9.3 permits it to be null
 * if and only if `historical` is true. Within a recorded group an individual
 * field may still be null: migration 241's `ace_dimensions_group_together`
 * admits exactly 0 or 4 nulls, so "all four null" is the recorded shape of a
 * group that records nothing.
 *
 * ICD 9.3 also requires a receiver to present null dimensions as "not
 * recorded", never as zero, a default or an inferred value. `triggers.ts`
 * honours that: see JUDGMENT CALL JC-1 there.
 */
export const AssessedDimensionsInputV1 = z.object({
  /** ICD 8.1, acquisition. */
  how_many: HowManyV1.nullable(),
  /** ICD 8.2, robustness. */
  how_often: HowOftenV1.nullable(),
  /** ICD 8.3, effectiveness. Limb 1 of the REC-03 TEM trigger reads this. */
  tem_outcome: TemOutcomeV1.nullable(),
  /** ICD 8.4, which dimension set the grade. Limb 2 of the trigger reads this. */
  limiting_factor: LimitingFactorV1.nullable(),
});

/** D.1: one competency graded within one assessment, with cited evidence. */
export const AssessedCompetencyInputV1 = z.object({
  competency_code: z.string().min(1),
  grade: GradeV1,
  /**
   * The observable behaviours the instructor cited for this competency, as
   * normalised codes (DEC-02: citation is the trigger; no valence tags).
   */
  cited_behaviours: z.array(z.string().min(1)),
  /**
   * The INT-08 capture group. It was carried opaque (`z.unknown()`) while no
   * MVP rule depended on it. REC-03 as amended by Andre Berger's ruling of
   * 4 September 2026 depends on two of its fields, so it is typed here and
   * carried onto `DrivingGradeEntry` by rule D-1, as that entry already
   * carries everything else the driving assessment holds.
   */
  dimensions: AssessedDimensionsInputV1.nullable().default(null),
});

/** D.1: one Guide assessment supplied to the policy (REC-01 window applies). */
export const AssessmentInputV1 = z.object({
  assessment_id: z.string().min(1),
  assessment_date: IsoDateV1,
  competencies: z.array(AssessedCompetencyInputV1).min(1),
});

/**
 * Section 3.3: the three distinct per-competency level fields. The effective
 * level for recommendation generation is always the assigned level; the
 * trainee-selected level never changes the recommendation input.
 */
export const CompetencyLevelInputV1 = z.object({
  competency_code: z.string().min(1),
  assigned_level: DevelopmentLevelV1,
  assigned_source: DevelopmentLevelSourceV1,
  selected_level: DevelopmentLevelV1.nullable().default(null),
});

/** D.1: one competency of the effective framework registry (CFX-01). */
export const FrameworkCompetencyInputV1 = z.object({
  code: z.string().min(1),
  /** Trainee-facing display name used by the D-6/D-6b templates. */
  name: z.string().min(1),
});

/** D.1: one observable behaviour of the effective framework registry. */
export const FrameworkBehaviourInputV1 = z.object({
  code: z.string().min(1),
  competency_code: z.string().min(1),
  origin: BehaviourOriginV1,
  /** Mandatory for an operator-derived behaviour (CFX-03); null otherwise. */
  parent_code: z.string().nullable().default(null),
});

/** D.1: the effective framework version in force, keyed by opaque identifier. */
export const FrameworkInputV1 = z.object({
  framework_id: z.string().min(1),
  /** The opaque published identifier; never a display label (CFX-01). */
  framework_version: z.string().min(1),
  /** Framework Listing Order, an Operator Competency last where held (CFX-05). */
  listing_order: z.array(z.string().min(1)).min(1),
  competencies: z.array(FrameworkCompetencyInputV1),
  behaviours: z.array(FrameworkBehaviourInputV1),
});

/** Operator versus reference provenance of a published content item (GOV-06). */
export const ContentProvenanceV1 = z.enum(['operator', 'reference']);

/** D.1: one published, applicable authored core pathway (GOV-01, GOV-06). */
export const PublishedPathwayInputV1 = z.object({
  pathway_id: z.string().min(1),
  competency_code: z.string().min(1),
  development_level: DevelopmentLevelV1,
  provenance: ContentProvenanceV1,
  /** Feeds the D-6 template slot [learning objective]. */
  learning_objective: z.string().min(1),
  /**
   * HP-15J: when the pathway's pack was imported (ISO 8601), the pack's age
   * for D-5. Where more than one pathway matches a competency and level, the
   * newest pack's wins, and a pathway without this value cannot take part in
   * that choice: selectCorePathway refuses rather than fall back to id order.
   */
  pack_imported_at: z.string().datetime({ offset: true }).optional(),
});

/** D.1: one published supplement object keyed by observable behaviour (8.2). */
export const SupplementObjectInputV1 = z.object({
  object_id: z.string().min(1),
  behaviour_code: z.string().min(1),
  provenance: ContentProvenanceV1,
  /** D-10: the object's position in its learning-objective sequence. */
  learning_objective_sequence: z.number().int().min(0),
});

/** D.1: the published-content index. */
export const ContentIndexInputV1 = z.object({
  pathways: z.array(PublishedPathwayInputV1),
  supplements: z.array(SupplementObjectInputV1),
});

/** D.1: earlier pathway history for the trainee (D-8 duplicate suppression). */
export const PathwayHistoryInputV1 = z.object({
  pathway_id: z.string().min(1),
  competency_code: z.string().min(1),
  development_level: DevelopmentLevelV1,
  status: PathwayStatusV1,
});

/** REC-08: tenant type selecting the mandatory-deadline default. */
export const TenantTypeV1 = z.enum(['airline', 'ato']);

/**
 * Appendix C tunables the policy consumes; defaults applied by the engine.
 * The supplement-cap knob is gone with the rule it configured: Appendix C
 * marks "Maximum supplement objects per pathway" withdrawn in DRAFT 0.10.7
 * (DEC-33). The rule D-9 optional cap is a different cap and stands.
 */
export const PolicyConfigInputV1 = z.object({
  /** Appendix C rule D-9 row; default 3, range 1-10. */
  optional_cap: z.number().int().min(1).max(10).optional(),
  /** Tenant deadline period override within the Appendix C ranges (REC-08). */
  deadline_period: z.string().min(1).optional(),
});

/** The complete D.1 input set for one policy run. */
export const PolicyInputV1 = z
  .object({
    /** Anchor of the REC-01 window: the processing date of the run. */
    reference_date: IsoDateV1,
    tenant_type: TenantTypeV1,
    config: PolicyConfigInputV1.default({}),
    assessments: z.array(AssessmentInputV1).min(1),
    development_levels: z.array(CompetencyLevelInputV1),
    framework: FrameworkInputV1,
    content: ContentIndexInputV1,
    pathway_history: z.array(PathwayHistoryInputV1).default([]),
  })
  .superRefine((input, ctx) => {
    const listing = new Set(input.framework.listing_order);
    const levels = new Set(input.development_levels.map((l) => l.competency_code));
    const behaviours = new Set(input.framework.behaviours.map((b) => b.code));
    for (const assessment of input.assessments) {
      for (const graded of assessment.competencies) {
        if (!listing.has(graded.competency_code)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `competency ${graded.competency_code} is not in the Framework Listing Order (CFX-05)`,
          });
        }
        if (!levels.has(graded.competency_code)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `competency ${graded.competency_code} has no development-level entry (INT-02, section 3.3)`,
          });
        }
        for (const cited of graded.cited_behaviours) {
          if (!behaviours.has(cited)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `cited behaviour ${cited} does not resolve in the framework registry (CFX-08)`,
            });
          }
        }
      }
    }
  });

export type IsoDate = z.infer<typeof IsoDateV1>;
export type AssessedDimensions = z.infer<typeof AssessedDimensionsInputV1>;
export type AssessedCompetencyInput = z.infer<typeof AssessedCompetencyInputV1>;
export type AssessmentInput = z.infer<typeof AssessmentInputV1>;
export type CompetencyLevelInput = z.infer<typeof CompetencyLevelInputV1>;
export type FrameworkInput = z.infer<typeof FrameworkInputV1>;
export type FrameworkBehaviourInput = z.infer<typeof FrameworkBehaviourInputV1>;
export type ContentProvenance = z.infer<typeof ContentProvenanceV1>;
export type PublishedPathwayInput = z.infer<typeof PublishedPathwayInputV1>;
export type SupplementObjectInput = z.infer<typeof SupplementObjectInputV1>;
export type ContentIndexInput = z.infer<typeof ContentIndexInputV1>;
export type PathwayHistoryInput = z.infer<typeof PathwayHistoryInputV1>;
export type TenantType = z.infer<typeof TenantTypeV1>;
export type PolicyInput = z.infer<typeof PolicyInputV1>;
/** The pre-validation input shape callers supply (defaults not yet applied). */
export type PolicyInputShape = z.input<typeof PolicyInputV1>;
export type Grade = z.infer<typeof GradeV1>;
export type GradeLabel = z.infer<typeof GradeLabelV1>;
export type DevelopmentLevel = z.infer<typeof DevelopmentLevelV1>;
export type PathwayStatus = z.infer<typeof PathwayStatusV1>;

/** REC-02: the driving evidence a recommendation must identify. */
export interface DrivingEvidence {
  source_assessment_id: string;
  assessment_date: IsoDate;
  grade: Grade;
  /** Canonical label derived from the grade (DEC-31, ICD 8.6). */
  grade_label: GradeLabel;
  /** Cited observable-behaviour codes in the source assessment (DEC-02). */
  cited_behaviours: string[];
}

/** Deadline inputs of a mandatory recommendation (Appendix C, REC-08). */
export interface DeadlineInputs {
  /** ISO 8601 calendar period from Appendix C, for example P3M. */
  period: string;
  /** Appendix C anchor: the moment of Mandatory recommendation entry. */
  runs_from: 'mandatory_recommendation_entry';
}

/** One ranked recommendation produced by the policy (REC-02, REC-05). */
export interface RecommendationOutput {
  competency_code: string;
  kind: 'mandatory' | 'optional';
  /**
   * REC-03 as amended (Andre Berger, 4 September 2026): true only for the
   * optional candidate a driving grade 2 WITHOUT the TEM trigger produces.
   * The ruling says such a candidate is "marked recommended using the existing
   * optional state", so `kind` stays `'optional'` and this marker is the only
   * thing that distinguishes it. It is present on every recommendation rather
   * than only where true, as `is_fallback` is, so a consumer reads a field
   * instead of an absence. A marked-recommended optional carries NO deadline,
   * as every optional does.
   */
  marked_recommended: boolean;
  /** D-4 rank; exactly one recommendation holds rank 1 (REC-05). */
  rank: number;
  /** The level generated against: the assigned level (section 3.3). */
  development_level: DevelopmentLevel;
  /** The attached published authored core pathway (D-5). */
  pathway_id: string;
  /** D-10 assembled supplement objects, deterministic order, uncapped. */
  supplement_object_ids: string[];
  driving_evidence: DrivingEvidence;
  /** True only for the D-3c guarantee recommendation (D-6b template). */
  is_fallback: boolean;
  /** The plain-language reason from the D-6 or D-6b template (REC-02). */
  explanation: string;
  /** Non-null for mandatory recommendations only (REC-03, REC-11, REC-12). */
  deadline: DeadlineInputs | null;
  policy_version: string;
}

/** D-3c zero-content outcome: exactly one, never a recommendation. */
export interface ContentGapNotification {
  kind: 'content_gap_notification';
  competency_code: string;
  development_level: DevelopmentLevel;
  source_assessment_id: string;
  /** Appendix H: shown to the trainee as Content in preparation. */
  trainee_label: string;
  logged_to: 'content_administrator';
}

/** First-class policy findings: gaps and suppressions, never exceptions. */
export type PolicyFinding =
  | {
      /** D-5/CFX-07: a cited behaviour with no supplement on it or its parent. */
      kind: 'supplement_content_gap';
      competency_code: string;
      behaviour_code: string;
      parent_code_tried: string | null;
    }
  | {
      /** D-5: a candidate dropped because no core pathway matches its level. */
      kind: 'core_pathway_missing';
      competency_code: string;
      development_level: DevelopmentLevel;
    }
  | {
      /** D-8: a candidate suppressed by an open pathway at the same level. */
      kind: 'duplicate_suppression';
      competency_code: string;
      open_pathway_id: string;
    }
  | {
      /** D-9: a lowest-ranked optional candidate suppressed by the cap. */
      kind: 'optional_cap_suppression';
      competency_code: string;
    };
/* D-10 raised no suppression finding since DRAFT 0.10.7: the supplement cap
   is withdrawn (DEC-33), so no object is ever held back at assembly. */

/** REC-01 audit record: what the window consumed and what it excluded. */
export interface WindowAudit {
  assessment_ids: string[];
  excluded_assessment_ids: string[];
}

/** The complete output of one policy run. */
export interface PolicyOutput {
  policy_version: string;
  framework_id: string;
  framework_version: string;
  reference_date: IsoDate;
  window: WindowAudit;
  recommendations: RecommendationOutput[];
  content_gap_notification: ContentGapNotification | null;
  findings: PolicyFinding[];
}
